/* product-personalizer.js — PDP personalized UX (MVP).
   Scoped to [data-product-section]. Vanilla, progressive enhancement.
   No external API, no localStorage/cookie. Form submits basic properties without JS. */
(function () {
  "use strict";

  document.querySelectorAll("[data-product-section]").forEach(initSection);

  function initSection(root) {
    const form =
      root.querySelector("#ProductForm") ||
      root.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const required = root.getAttribute("data-required") === "true";
    const personalizer = root.querySelector("[data-personalizer]");
    const errorEl = root.querySelector("[data-personalizer-error]");

    /* ---------- Minimal money formatter (uses shop money_format) ---------- */
    const priceWrap = root.querySelector("[data-price]");
    const moneyFormat = priceWrap
      ? priceWrap.getAttribute("data-money-format") || "${{amount}}"
      : "${{amount}}";
    function formatMoney(cents) {
      const value = cents | 0;
      function withDecimals(precision, thousands, decimal) {
        const num = (value / 100).toFixed(precision);
        const parts = num.split(".");
        parts[0] = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + (thousands || ","),
        );
        return parts.length > 1 ? parts.join(decimal || ".") : parts[0];
      }
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
        switch (token) {
          case "amount":
            return withDecimals(2, ",", ".");
          case "amount_no_decimals":
            return withDecimals(0, ",", ".");
          case "amount_with_comma_separator":
            return withDecimals(2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return withDecimals(0, ".", ",");
          default:
            return withDecimals(2, ",", ".");
        }
      });
    }

    /* ---------- Variant selection ---------- */
    const idInput = root.querySelector("[data-variant-id]");
    const jsonEl = root.querySelector("[data-variant-json]");
    let variants = [];
    if (jsonEl) {
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        variants = [];
      }
    }
    const atc = root.querySelector("[data-atc]");
    const atcText = root.querySelector("[data-atc-text]");

    function selectedOptions() {
      const picks = [];
      root
        .querySelectorAll("input[data-option-input]:checked")
        .forEach(function (input) {
          const pos = parseInt(input.getAttribute("data-option-position"), 10);
          picks[pos - 1] = input.value;
        });
      return picks;
    }

    function findVariant(picks) {
      return variants.find(function (v) {
        return (v.options || []).every(function (opt, i) {
          return picks[i] === undefined || String(opt) === String(picks[i]);
        });
      });
    }

    function updateVariant() {
      if (!variants.length) return;
      const picks = selectedOptions();
      const variant = findVariant(picks) || variants[0];
      if (!variant) return;
      if (idInput) idInput.value = variant.id;

      // Price
      if (priceWrap) {
        const current = priceWrap.querySelector(".price__current");
        if (current) current.textContent = formatMoney(variant.price);
      }

      // Availability -> ATC
      if (atc) {
        atc.disabled = !variant.available;
        if (atcText) {
          atcText.textContent = variant.available
            ? atc.getAttribute("data-add-label") || "Add to cart"
            : atc.getAttribute("data-sold-label") || "Sold out";
        }
      }

      showVariantMedia(variant);

      /**
       * Tell any app block on the page which variant is now selected.
       *
       * `idInput.value = ...` above is a property assignment: it fires no `change` event and does
       * not touch the attribute, so nothing outside this file can observe it. The Perso-Craft
       * personalizer needs it -- its live preview composites onto a photograph bound per variant,
       * so without this the buyer picks a different colour and keeps seeing the old one.
       *
       * `variant:change` with `detail.variant` is the Dawn convention, so an app written for any
       * OS 2.0 theme already knows how to listen for it.
       */
      document.dispatchEvent(
        new CustomEvent("variant:change", { detail: { variant: variant } })
      );
    }

    /**
     * Swap the gallery to the photo that belongs to the chosen variant.
     *
     * `featured_media` is the modern field and the one Avixa's push populates; `featured_image` is
     * the legacy alias and is null on products whose media were attached the current way, so
     * reading it alone would look like "the product has no variant photo" on every product that
     * does. A variant with neither keeps whatever the gallery is showing — swapping back to the
     * product hero would throw away a picture the buyer deliberately opened.
     */
    function showVariantMedia(variant) {
      const main = root.querySelector("[data-main-image]");
      if (!main) return;

      const media = variant.featured_media;
      const src =
        (media && media.preview_image && media.preview_image.src) ||
        (variant.featured_image && variant.featured_image.src);
      if (!src) return;

      // Shopify serves protocol-relative URLs here and accepts a width parameter; ask for the
      // size this slot actually renders rather than shipping the 1600px original.
      const sized = src.indexOf("width=") === -1
        ? src + (src.indexOf("?") === -1 ? "?" : "&") + "width=1200"
        : src;
      if (main.getAttribute("src") === sized) return;

      main.setAttribute("src", sized);
      const alt = (media && media.alt) || (variant.featured_image && variant.featured_image.alt);
      if (alt) main.setAttribute("alt", alt);
    }

    root.querySelectorAll("input[data-option-input]").forEach(function (input) {
      input.addEventListener("change", function () {
        // visual state on chips
        const fieldset = input.closest("fieldset");
        if (fieldset)
          fieldset.querySelectorAll(".chip").forEach(function (c) {
            c.classList.remove("is-selected");
          });
        const label = input.closest(".chip");
        if (label) label.classList.add("is-selected");
        updateVariant();
      });
    });

    /* ---------- Required-field validation ---------- */
    function validate() {
      if (!required || !personalizer) return true;
      let ok = true;
      personalizer.querySelectorAll("[required]").forEach(function (el) {
        if (el.type === "radio") {
          const group = personalizer.querySelectorAll(
            'input[name="' + CSS.escape(el.name) + '"]',
          );
          if (
            ![].some.call(group, function (r) {
              return r.checked;
            })
          )
            ok = false;
        } else if (!el.value.trim()) {
          ok = false;
        }
      });
      if (errorEl) errorEl.hidden = ok;
      return ok;
    }

    form.addEventListener(
      "submit",
      function (e) {
        if (!validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true,
    );

    /* ---------- Preview (local only) ---------- */
    const preview = root.querySelector("[data-preview]");
    if (preview) {
      const trigger = preview.querySelector("[data-preview-trigger]");
      const states = preview.querySelectorAll("[data-preview-state]");
      const nameOut = preview.querySelector("[data-preview-name]");
      const msgOut = preview.querySelector("[data-preview-message]");
      const previewIdInput = root.querySelector('[data-prop="Preview ID"]');
      const renderJobInput = root.querySelector('[data-prop="_Render Job ID"]');

      function setState(name) {
        states.forEach(function (s) {
          s.hidden = s.getAttribute("data-preview-state") !== name;
        });
      }
      function getVal(prop) {
        const el =
          personalizer &&
          personalizer.querySelector('[data-pf="' + prop + '"]');
        if (!el) return "";
        if (el.type === "radio") {
          const checked = personalizer.querySelector(
            '[data-pf="' + prop + '"]:checked',
          );
          return checked ? checked.value : "";
        }
        return el.value || "";
      }

      if (trigger) {
        trigger.addEventListener("click", function () {
          if (!validate()) {
            setState("error");
            return;
          }
          setState("loading");
          const stamp = Date.now();
          window.setTimeout(function () {
            if (nameOut) nameOut.textContent = getVal("Custom Name");
            const msg = getVal("Custom Message");
            if (msgOut) msgOut.textContent = msg;
            if (previewIdInput) previewIdInput.value = "preview-" + stamp;
            if (renderJobInput) renderJobInput.value = "draft-" + stamp;
            setState("success");
          }, 400);
        });
      }
    }

    /* ---------- Sticky mobile ATC visibility ---------- */
    const sticky = root.querySelector("[data-sticky-atc]");
    if (sticky && atc && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            sticky.hidden = entry.isIntersecting;
          });
        },
        { rootMargin: "0px 0px -120px 0px" },
      );
      io.observe(atc);
    }

    // Initial sync
    updateVariant();
  }
})();
